"use client";

import type { ReactNode } from "react";
import NextLink from "next/link";
import { Show } from "@clerk/nextjs";
import { Link, Typography } from "@cappelendamm/design-system/react";
import { formatPrice } from "@cappelendamm/e-commerce-services";
import { useSignInRedirect } from "@/auth/hooks/useSignInRedirect";
import { type PriceList } from "@/products/pricing";
import { useMemberPriceOffer } from "@/products/useMemberPriceOffer";

type MemberPriceProps = {
  originalItemPrice: number | null | undefined;
  subjectToVAT: boolean | null | undefined;
  pricelists?: PriceList[] | null | undefined;
  /** "card" in product lists, "page" on the product page */
  size?: "card" | "page";
  /** Where a guest can become a member, on sites that have book clubs */
  joinHref?: string;
  /** The usual price display, shown when there is no member price */
  children: ReactNode;
};

/**
 * Leads with the member price when the product has one, highlighted in the
 * universe's offer colour. Cards use a separate tag above the title; the product
 * page labels the price inline. Guests see what members
 * pay; members see their own price. The ordinary price stays visible but
 * smaller, and is not struck through, because it is what everyone without a
 * membership pays.
 */
export function MemberPrice({
  originalItemPrice,
  subjectToVAT,
  pricelists,
  size = "card",
  joinHref,
  children,
}: MemberPriceProps) {
  const offer = useMemberPriceOffer({
    originalItemPrice,
    subjectToVAT,
    pricelists,
  });

  if (!offer) return children;

  if (size === "card") {
    return (
      <div className="flex min-w-0 flex-col items-start gap-0.5 font-normal">
        <Typography
          as="span"
          variant="title-md"
          className="bg-(--cvr-tag-background--promoted) font-semibold whitespace-nowrap text-(--cvr-tag-contrast--promoted) tabular-nums"
        >
          <span className="sr-only">Medlemspris </span>
          {formatPrice(offer.memberPrice)}
        </Typography>
        <Typography
          as="p"
          variant="body-sm"
          className="whitespace-nowrap text-gray-600"
        >
          {offer.comparisonIsCampaign ? "Tilbudspris" : "Før"}{" "}
          {formatPrice(offer.comparisonPrice)}
        </Typography>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-start gap-2 font-normal">
      <p className="flex flex-wrap items-center gap-x-3 gap-y-1">
        {/* The price in a pill of the universe's offer colour */}
        <span className="rounded-full bg-(--cvr-tag-background--promoted) px-4 py-0.5 text-[2rem] leading-snug font-semibold whitespace-nowrap text-(--cvr-tag-contrast--promoted) tabular-nums">
          {formatPrice(offer.memberPrice)}
        </span>
        <span className="text-base font-semibold whitespace-nowrap">
          {offer.isMember ? "Din medlemspris" : "Medlemspris"}
        </span>
      </p>
      <p className="text-base text-gray-600">
        {offer.comparisonIsCampaign ? "Tilbudspris" : "Før"}{" "}
        {formatPrice(offer.comparisonPrice)}
      </p>
      {!offer.isMember ? <MemberPriceNote joinHref={joinHref} /> : null}
    </div>
  );
}

/** Under the price on the product page: who the price is for, and how to get it */
function MemberPriceNote({ joinHref }: { joinHref?: string }) {
  const signIn = useSignInRedirect({ returnHere: true });

  const signInButton = (
    <Link
      as="button"
      type="button"
      onClick={signIn}
      variant="inline"
      className="cursor-pointer font-semibold whitespace-nowrap"
    >
      Logg inn
    </Link>
  );
  const joinLink = (label: string) =>
    joinHref ? (
      <Link
        as={NextLink}
        href={joinHref}
        variant="inline"
        className="font-semibold whitespace-nowrap"
      >
        {label}
      </Link>
    ) : null;

  // Without a club chooser this is not a club's own site, so name the clubs
  return (
    <p className="text-sm">
      Gjelder kun for {joinHref ? "medlemmer" : "bokklubbmedlemmer"}.{" "}
      <Show when="signed-out">
        {joinHref ? (
          <>
            {signInButton} eller {joinLink("bli medlem")}.
          </>
        ) : (
          <>Er du medlem? {signInButton}.</>
        )}
      </Show>
      <Show when="signed-in">{joinLink("Bli medlem")}</Show>
    </p>
  );
}
